/**
 * CapabilityTogglePanel — Account Detail panel for toggling per-user
 * capability flags (is_customer / is_provider / is_admin).
 *
 * Renders on EVERY account detail view (admin / customer / PooPartner)
 * because the toggles are the only mechanism for promoting a user into
 * a multi-role account.
 *
 * Server-side guards mirrored here:
 *   - "At least one capability" — Save disabled when all three are off.
 *   - "Admins cannot demote themselves" — the is_admin checkbox is
 *     disabled (with tooltip) when the acting admin is editing their
 *     own account AND currently has is_admin=True.
 *
 * IMPORTANT: toggles are pure persistence operations. Booking math is
 * unaffected. Enabling a flag auto-creates the matching profile shell
 * server-side; existing profile data is preserved across toggle cycles.
 */
import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { ShieldCheck, Loader2 } from "lucide-react";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useConfig } from "@/context/ConfigContext";

export default function CapabilityTogglePanel({ detail, onSaved }) {
  const { user: actingAdmin } = useAuth();
  const { providerDisplayLabel } = useConfig();

  const initial = useMemo(
    () => ({
      is_customer: !!detail?.is_customer,
      is_provider: !!detail?.is_provider,
      is_admin: !!detail?.is_admin,
    }),
    [detail]
  );

  const [draft, setDraft] = useState(initial);
  const [saving, setSaving] = useState(false);

  // Re-sync the draft when the parent re-fetches the detail (after a
  // successful save the parent calls onSaved with the updated payload).
  useEffect(() => {
    setDraft(initial);
  }, [initial]);

  const isSelf =
    !!actingAdmin && !!detail && actingAdmin.id === detail.id;
  const adminToggleLocked = isSelf && initial.is_admin;

  const noneActive =
    !draft.is_customer && !draft.is_provider && !draft.is_admin;

  const dirty =
    draft.is_customer !== initial.is_customer ||
    draft.is_provider !== initial.is_provider ||
    draft.is_admin !== initial.is_admin;

  const update = (key, val) => {
    if (key === "is_admin" && adminToggleLocked) return; // anti-lockout
    setDraft((prev) => ({ ...prev, [key]: !!val }));
  };

  const submit = async () => {
    if (!dirty || noneActive) return;
    setSaving(true);
    try {
      const { data } = await api.patch(
        `/admin/accounts/${detail.id}/capabilities`,
        draft
      );
      toast.success("Capabilities updated.");
      onSaved?.(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className="mt-5" data-testid="capability-toggle-panel">
      <hr className="mb-5 border-pp-border" />
      <div className="mb-3 flex items-center gap-2">
        <ShieldCheck className="h-5 w-5 text-brand-orange" aria-hidden="true" />
        <h4 className="font-heading text-lg font-semibold text-pp-text">
          Capabilities
        </h4>
      </div>
      <p
        className="mb-4 text-xs text-pp-text-muted"
        data-testid="capability-toggle-subtitle"
      >
        Multi-role aware. Enabling a flag auto-creates the matching profile.
        Toggles do not change booking math.
      </p>

      <div className="grid gap-3 sm:grid-cols-3">
        <CapabilityRow
          testid="capability-toggle-customer"
          label="Customer"
          checked={draft.is_customer}
          onChange={(v) => update("is_customer", v)}
        />
        <CapabilityRow
          testid="capability-toggle-provider"
          label={providerDisplayLabel}
          checked={draft.is_provider}
          onChange={(v) => update("is_provider", v)}
        />
        <CapabilityRow
          testid="capability-toggle-admin"
          label="Admin"
          checked={draft.is_admin}
          onChange={(v) => update("is_admin", v)}
          disabled={adminToggleLocked}
          disabledHint="You can't demote yourself. Have another admin do it."
        />
      </div>

      {noneActive && (
        <p
          className="mt-3 text-xs font-semibold text-red-600"
          data-testid="capability-toggle-error-empty"
        >
          A user must have at least one active capability.
        </p>
      )}

      <div className="mt-4 flex justify-end">
        <Button
          type="button"
          onClick={submit}
          disabled={!dirty || saving || noneActive}
          className="h-11 rounded-full bg-pp-orange px-6 text-pp-cta-text hover:bg-pp-orange-hover"
          data-testid="capability-toggle-save"
        >
          {saving ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Saving…
            </>
          ) : (
            "Save capabilities"
          )}
        </Button>
      </div>
    </section>
  );
}

function CapabilityRow({ testid, label, checked, onChange, disabled, disabledHint }) {
  const inner = (
    <label
      className={
        "flex h-11 cursor-pointer items-center gap-3 rounded-full border border-pp-border px-4 transition-colors " +
        (disabled
          ? "cursor-not-allowed bg-pp-surface2/50 opacity-70"
          : "bg-pp-surface hover:border-brand-orange/50")
      }
      data-testid={testid}
    >
      <Checkbox
        checked={checked}
        onCheckedChange={onChange}
        disabled={disabled}
        data-testid={`${testid}-input`}
      />
      <span className="text-sm font-semibold text-pp-text">{label}</span>
    </label>
  );
  if (!disabled || !disabledHint) return inner;
  return (
    <TooltipProvider delayDuration={150}>
      <Tooltip>
        <TooltipTrigger asChild>{inner}</TooltipTrigger>
        <TooltipContent data-testid={`${testid}-tooltip`}>
          {disabledHint}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
